import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

import { ISystemPreferences, IUserSession } from 'src/app/core/dto';
import { ClientDataStore } from './clientData.store';
import { selectUserSession } from './store/user/selector';
import { TransService } from './trans.service';
import { UnitsService } from './units.service';

interface IEffectivePreferences {
  language: string;
  inchesUnit: string;
}

/**
 * Resolves the effective per-user preferences (language, units) from the
 * combination of the user's overrides and the system defaults, and applies
 * them to {@link TransService} and {@link UnitsService}.
 *
 * The user's override (if any) wins; otherwise the system default is used.
 * Both inputs are reactive: a change to system preferences propagates to
 * users who haven't overridden, and changes to a user's override take effect
 * immediately for that user.
 */
@Injectable({
  providedIn: 'root',
})
export class UserPreferencesService {
  private readonly effective$ = new BehaviorSubject<IEffectivePreferences>({
    language: '',
    inchesUnit: 'in',
  });

  readonly preferences$ = this.effective$.asObservable();

  constructor(
    private clientDataStore: ClientDataStore,
    private store: Store,
    private transService: TransService,
    private unitsService: UnitsService
  ) {
    combineLatest([
      this.clientDataStore.SelectSystemPreferences(),
      this.store.select(selectUserSession),
    ])
      .pipe(
        filter(([sysPrefs, session]) => !!sysPrefs && !!session),
        map(([sysPrefs, session]) => this.resolve(sysPrefs, session)),
        distinctUntilChanged(
          (a, b) => a.language === b.language && a.inchesUnit === b.inchesUnit
        )
      )
      .subscribe((prefs) => {
        this.effective$.next(prefs);
        if (prefs.language) {
          this.transService.setLanguage(prefs.language);
        }
        if (prefs.inchesUnit) {
          this.unitsService.currentInchesKey = prefs.inchesUnit;
        }
      });
  }

  private resolve(sysPrefs: ISystemPreferences, session: IUserSession): IEffectivePreferences {
    // Always resolve from the live system prefs (not session.language, which is
    // a snapshot from session-fetch time). That way clearing the override falls
    // back to the *current* system default, and a system-default change reaches
    // users without an override without anyone reloading.
    return {
      language:
        session.userLanguage && session.userLanguage.length > 0
          ? session.userLanguage
          : sysPrefs.systemLanguage,
      inchesUnit:
        session.userInchesUnit && session.userInchesUnit.length > 0
          ? session.userInchesUnit
          : sysPrefs.inchesUnit,
    };
  }
}
